import { z } from 'zod'

const id = z.string().min(1)
const reason = z.string().trim().min(1).max(2000)
const target = z.object({ catalogClassId: id, catalogSubtypeId: id.nullable() })
export const ModelReconciliationRequest = z.strictObject({
  catalog: z
    .array(
      z.strictObject({
        entityType: z.enum(['model', 'subtype']),
        entityId: id,
        ...target.shape,
        retired: z.boolean().optional(),
        reason,
      })
    )
    .default([]),
  classes: z.array(z.strictObject({ classId: id, ...target.shape, reason })).default([]),
  identities: z
    .array(
      z.strictObject({
        identityId: id,
        ...target.shape,
        divisionCode: z.string().trim().min(1),
        retired: z.boolean().optional(),
        reason,
      })
    )
    .default([]),
  decisions: z
    .array(
      z.strictObject({
        classId: id,
        identityId: id.nullable(),
        reviewedDivisionCode: id.nullable(),
        reason,
      })
    )
    .default([]),
  historicalModels: z
    .array(
      z.strictObject({
        id: z.uuid(),
        name: z.string().trim().min(1),
        categoryCode: id,
        description: z.string().nullable().default(null),
        retired: z.literal(true).default(true),
        reason,
      })
    )
    .default([]),
  historicalSubtypes: z
    .array(
      z.strictObject({
        id: z.uuid(),
        catalogClassId: id,
        name: z.string().trim().min(1),
        description: z.string().nullable().default(null),
        retired: z.literal(true).default(true),
        reason,
      })
    )
    .default([]),
})
export const ApplyModelReconciliationRequest = ModelReconciliationRequest.extend({
  reviewToken: z.string().min(1),
  reviewedAt: z.string().datetime(),
})
export const ReconciliationProblem = z.object({
  entityType: z.enum(['model', 'subtype', 'class', 'identity', 'decision']),
  entityId: id,
  code: z.string(),
  message: z.string(),
})
export const ReconciliationRow = z.object({
  entityType: z.enum(['model', 'subtype', 'class', 'identity']),
  id,
  label: z.string(),
  catalogClassId: id.nullable(),
  catalogSubtypeId: id.nullable(),
  showId: id.nullable(),
  seriesId: id.nullable(),
  divisionCode: z.string().nullable(),
  retired: z.boolean(),
  values: z.record(z.string(), z.unknown()),
})
export const ModelReconciliationInventory = z.object({
  catalogIdentity: id,
  catalogRevision: z.number().int(),
  enforced: z.boolean(),
  fingerprint: z.string(),
  rows: z.array(ReconciliationRow),
  problems: z.array(ReconciliationProblem),
  warnings: z.array(ReconciliationProblem),
  decisions: z.array(
    z.object({
      classId: id,
      identityId: id.nullable(),
      reviewedDivisionCode: id.nullable(),
      showId: id,
      seriesId: id,
      revision: z.number().int(),
    })
  ),
})
export const ReconciliationChange = z.object({
  entityType: z.enum(['model', 'subtype', 'class', 'identity', 'decision']),
  entityId: id,
  reason,
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()),
})
export const ModelReconciliationPreview = z.object({
  reviewToken: z.string(),
  reviewedAt: z.string().datetime(),
  fingerprint: z.string(),
  changes: z.array(ReconciliationChange),
  blockers: z.array(ReconciliationProblem),
  remaining: z.array(ReconciliationProblem),
  canApply: z.boolean(),
  warnings: z.array(ReconciliationProblem),
})
export const ModelReconciliationResult = z.object({
  receiptId: id,
  alreadyApplied: z.boolean(),
  changed: z.number().int(),
  remaining: z.array(ReconciliationProblem),
})
export type TModelReconciliationRequest = z.infer<typeof ModelReconciliationRequest>
export type TModelReconciliationInventory = z.infer<typeof ModelReconciliationInventory>
export type TModelReconciliationPreview = z.infer<typeof ModelReconciliationPreview>
export type TReconciliationChange = z.infer<typeof ReconciliationChange>
export type TReconciliationProblem = z.infer<typeof ReconciliationProblem>
export type TModelReconciliationResult = z.infer<typeof ModelReconciliationResult>
